import type { DesignModel } from '@forgd/contract/types'
import type { MaybeRefOrGetter } from 'vue'
import { useDesignTokenomicsApi } from '#design-tokenomics/composables/useDesignTokenomicsApi'
import { hashModel } from '#design-tokenomics/utils/draft'
import { useQuery } from '@pinia/colada'

/**
 * Dashboard figures for a model.
 *
 * The endpoint is a pure function of the model, so the cache is keyed by the model's content:
 * adjusting a value fetches once, undoing it comes back from cache, and two visitors on the same
 * untouched model share an entry.
 */
export function useDesignDashboard(model: MaybeRefOrGetter<DesignModel | null>) {
  const api = useDesignTokenomicsApi()
  const current = computed(() => toValue(model))

  const query = useQuery({
    key: () => ['design-tokenomics', 'dashboard', current.value ? hashModel(current.value) : 'none'],
    query: () => api.fetchDashboard(current.value!),
    enabled: () => current.value !== null,
    placeholderData: previous => previous,
  })

  return {
    dashboard: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refresh: query.refetch,
  }
}
